import { supabase } from './supabase'
import { SUPABASE_URL } from '../utils/constants'

export interface BackfillEmbeddingsParams {
  /** Max rows per table (suppliers, news, mitigations) missing embeddings. Default 100, max 500. */
  limit?: number
}

export interface BackfillEmbeddingsResponse {
  message: string
  embedded: number
  failed: number
  remainingHint?: string
  lastError?: string
}

export interface BackfillUntilDoneProgress {
  round: number
  embedded: number
  failed: number
  last: BackfillEmbeddingsResponse
}

export interface BackfillUntilDoneResult {
  embedded: number
  failed: number
  rounds: number
  message: string
  lastError?: string
}

const DEFAULT_LIMIT = 100
const ROUND_PAUSE_MS = 500
const MAX_ROUNDS = 200

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

export async function invokeBackfillEmbeddings(
  params: BackfillEmbeddingsParams = {}
): Promise<BackfillEmbeddingsResponse> {
  const { limit = DEFAULT_LIMIT } = params
  const {
    data: { session },
    error: sessionError,
  } = await supabase.auth.getSession()
  if (sessionError || !session?.access_token) {
    throw new Error('Not authenticated. Please sign in again.')
  }
  const url = `${SUPABASE_URL}/functions/v1/backfill-embeddings`
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${session.access_token}`,
    },
    body: JSON.stringify({ limit }),
  })
  const data = await res.json().catch(() => ({}))
  if (!res.ok) {
    const message =
      data?.error ??
      res.statusText ??
      'Backfill failed. Ensure you are an admin and OpenAI is configured in AI Settings.'
    throw new Error(message)
  }
  return data as BackfillEmbeddingsResponse
}

/**
 * Keep calling backfill-embeddings until no remainingHint (or safety cap).
 * Edge Function still batches ~100 rows/table per invocation.
 */
export async function runBackfillEmbeddingsUntilDone(options?: {
  limit?: number
  maxRounds?: number
  pauseMs?: number
  onProgress?: (p: BackfillUntilDoneProgress) => void
}): Promise<BackfillUntilDoneResult> {
  const limit = options?.limit ?? DEFAULT_LIMIT
  const maxRounds = options?.maxRounds ?? MAX_ROUNDS
  const pauseMs = options?.pauseMs ?? ROUND_PAUSE_MS

  let embedded = 0
  let failed = 0
  let rounds = 0
  let lastError: string | undefined
  let lastMessage = 'No embedding work required.'

  while (rounds < maxRounds) {
    if (rounds > 0) await sleep(pauseMs)
    const last = await invokeBackfillEmbeddings({ limit })
    rounds++
    embedded += last.embedded ?? 0
    failed += last.failed ?? 0
    if (last.lastError) lastError = last.lastError
    lastMessage = last.message
    options?.onProgress?.({ round: rounds, embedded, failed, last })

    if (!last.remainingHint) break
    // Safety: if a batch returns remainingHint but did no work, avoid infinite loop
    if ((last.embedded ?? 0) === 0 && (last.failed ?? 0) === 0) break
  }

  const capped = rounds >= maxRounds
  const message = capped
    ? `Stopped after ${rounds} batches (safety cap). Embedded ${embedded}, failed ${failed}. Run again if rows remain.`
    : embedded === 0 && failed === 0
      ? lastMessage
      : `Embeddings complete after ${rounds} batch(es). Embedded ${embedded}, failed ${failed}.`

  return { embedded, failed, rounds, message, lastError }
}
